/**
 * Worklist Time Utilities
 *
 * Frontend utilities for calculating time-based indicators for worklist items.
 * Used for visual urgency indicators based on SLA.
 *
 * Color scheme (fixed values):
 * - Green: >50% time remaining
 * - Yellow: 25-50% time remaining
 * - Red: <25% time remaining
 * - Expired: Past due date
 *
 * @module domain/worklist
 */

/**
 * Time indicator color types
 */
export type TimeIndicatorColor = 'green' | 'yellow' | 'red' | 'expired'

/**
 * Time indicator result
 */
export type TimeIndicator = {
  readonly color: TimeIndicatorColor
  readonly percentRemaining: number
  readonly hoursRemaining: number
  readonly isOverdue: boolean
}

/**
 * Calculate time indicator based on due date and SLA hours
 *
 * @param dueAt - The due date for the worklist item
 * @param slaHours - The SLA hours configured for the institution (default: 48)
 * @param createdAt - When the worklist item was created (optional, for accurate percentage)
 * @returns TimeIndicator with color, percentage, and hours remaining
 */
export function calculateTimeIndicator(
  dueAt: string | Date | null,
  slaHours: number = 48,
  createdAt?: string | Date | null
): TimeIndicator {
  // If no due date, assume plenty of time (green)
  if (!dueAt) {
    return {
      color: 'green',
      percentRemaining: 100,
      hoursRemaining: slaHours,
      isOverdue: false,
    }
  }

  const now = new Date()
  const dueTime = new Date(dueAt).getTime()
  const nowTime = now.getTime()

  // Calculate hours remaining
  const msRemaining = dueTime - nowTime
  const hoursRemaining = msRemaining / (1000 * 60 * 60)

  // Check if overdue
  if (nowTime > dueTime) {
    return {
      color: 'expired',
      percentRemaining: 0,
      hoursRemaining: Math.round(hoursRemaining * 10) / 10, // Negative value
      isOverdue: true,
    }
  }

  // Calculate percentage remaining
  // If we have createdAt, calculate from actual start time
  // Otherwise, estimate based on SLA hours
  const totalMs = slaHours * 60 * 60 * 1000
  let percentRemaining: number

  if (createdAt) {
    const createdTime = new Date(createdAt).getTime()
    const elapsed = nowTime - createdTime
    const total = dueTime - createdTime
    percentRemaining = Math.max(0, Math.min(100, ((total - elapsed) / total) * 100))
  } else {
    percentRemaining = Math.max(0, Math.min(100, (msRemaining / totalMs) * 100))
  }

  // Determine color based on percentage
  let color: TimeIndicatorColor
  if (percentRemaining > 50) {
    color = 'green'
  } else if (percentRemaining > 25) {
    color = 'yellow'
  } else {
    color = 'red'
  }

  return {
    color,
    percentRemaining: Math.round(percentRemaining * 10) / 10,
    hoursRemaining: Math.round(hoursRemaining * 10) / 10,
    isOverdue: false,
  }
}

/**
 * Get human-readable label for time indicator
 *
 * @param indicator - The time indicator
 * @returns Human-readable string in Spanish
 */
export function getTimeIndicatorLabel(indicator: TimeIndicator): string {
  if (indicator.isOverdue) {
    const hoursOverdue = Math.abs(indicator.hoursRemaining)
    if (hoursOverdue < 1) {
      return `Vencido hace ${Math.round(hoursOverdue * 60)} min`
    } else if (hoursOverdue < 24) {
      return `Vencido hace ${Math.round(hoursOverdue)}h`
    } else {
      return `Vencido hace ${Math.round(hoursOverdue / 24)}d`
    }
  }

  if (indicator.hoursRemaining < 1) {
    return `${Math.round(indicator.hoursRemaining * 60)} min restantes`
  } else if (indicator.hoursRemaining < 24) {
    return `${Math.round(indicator.hoursRemaining)}h restantes`
  } else {
    return `${Math.round(indicator.hoursRemaining / 24)}d restantes`
  }
}

/**
 * Get short label for time indicator (for compact display)
 *
 * @param indicator - The time indicator
 * @returns Short human-readable string
 */
export function getTimeIndicatorShortLabel(indicator: TimeIndicator): string {
  if (indicator.isOverdue) {
    return 'Vencido'
  }

  if (indicator.hoursRemaining < 1) {
    return `${Math.round(indicator.hoursRemaining * 60)}m`
  } else if (indicator.hoursRemaining < 24) {
    return `${Math.round(indicator.hoursRemaining)}h`
  } else {
    return `${Math.round(indicator.hoursRemaining / 24)}d`
  }
}
